import { PartnerUser } from '../types/partner';

const PARTNER_TOKEN_KEY = 'partner_token';
const PARTNER_REFRESH_TOKEN_KEY = 'partner_refresh_token';

/**
 * Get partner token from localStorage
 */
export const getPartnerToken = (): string | null => {
  return localStorage.getItem(PARTNER_TOKEN_KEY);
};

/**
 * Set partner token in localStorage
 */
export const setPartnerToken = (token: string): void => {
  localStorage.setItem(PARTNER_TOKEN_KEY, token);
};

/**
 * Remove partner token from localStorage
 */
export const removePartnerToken = (): void => {
  localStorage.removeItem(PARTNER_TOKEN_KEY);
  localStorage.removeItem(PARTNER_REFRESH_TOKEN_KEY);
};

/**
 * Get partner refresh token from localStorage
 */
export const getPartnerRefreshToken = (): string | null => {
  return localStorage.getItem(PARTNER_REFRESH_TOKEN_KEY);
};

/**
 * Set partner refresh token in localStorage
 */
export const setPartnerRefreshToken = (token: string): void => {
  localStorage.setItem(PARTNER_REFRESH_TOKEN_KEY, token);
};

/**
 * Parse JWT token and extract payload
 */
export const parsePartnerToken = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing partner token:', error);
    return null;
  }
};

/**
 * Check if partner token is expired
 */
export const isPartnerTokenExpired = (token: string): boolean => {
  const payload = parsePartnerToken(token);
  if (!payload || !payload.exp) {
    return true;
  }
  
  const expirationTime = payload.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  
  return currentTime >= expirationTime;
};

/**
 * Get current partner user from token
 */
export const getCurrentPartnerUser = (): PartnerUser | null => {
  const token = getPartnerToken();
  if (!token) {
    return null;
  }

  if (isPartnerTokenExpired(token)) {
    removePartnerToken();
    return null;
  }

  const payload = parsePartnerToken(token);
  if (!payload) {
    return null;
  }

  return {
    id: payload.sub || payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'], // Guid as string
    email: payload.email || payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'],
    displayName: payload.name || payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] || '',
    role: payload.role || payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
    partnerId: parseInt(payload.partner_id),
    emailVerified: payload.email_verified === 'true' || payload.email_verified === true,
    mfaEnabled: payload.mfa_enabled === 'true' || payload.mfa_enabled === true,
  };
};

/**
 * Check if partner user has a specific role
 */
export const hasPartnerRole = (user: PartnerUser | null, role: string): boolean => {
  if (!user) {
    return false;
  }
  
  // Check exact role match
  if (user.role === role) {
    return true;
  }
  
  // partner_admin has all permissions
  if (user.role === 'partner_admin') {
    return true;
  }
  
  return false;
};

